import { decode } from '@scalar-org/tokens'
import { accessTokenPayloadSchema } from '@scalar-org/entities/token'

import { Command } from 'commander'
import { emailPasswordAuth, generateCallbackEndpoint } from './helpers'
import getPort from 'get-port'
import as from 'ansis'
import { setAuthData } from '@/helpers'
import { config } from '@/config'
import { output } from '@/helpers/std-out'
import { generateAccessByPersonalToken } from '@/domains/auth/login/helpers/personal-token-generate-access'
import { openBrowser } from '@/helpers/url-opener'

export const LoginCommand = () => {
  const cmd = new Command('login')

  cmd.description('Login to scalar')
  cmd.option('--email <email>', 'Email')
  cmd.option('--password <password>', 'Password')
  cmd.option('--token <token>', 'Personal token')
  cmd.action(async ({ email, password, token: _token }) => {
    // Attempt to fetch a token from the environment variables
    const token = process.env.SCALAR_API_KEY || _token
    // If personal token is provided we try to generate a new access token from the personal token
    if (token) {
      // Try to generate a new access token
      try {
        const { accessToken } = await generateAccessByPersonalToken({
          personalToken: token,
        })
        const { email: userEmail } = decode(
          accessToken,
          accessTokenPayloadSchema,
        )

        output
          .info()
          .message(`Logged in successfully as ${as.cyan(userEmail ?? '')}`)
          .print()
      } catch (err: any) {
        return output
          .error()
          .title('Invalid token')
          .message(err.message ?? 'Please provide a valid personal token')
          .exit('error')
      }

      return
    }

    // Allow user to provide username/password combination to login for ci flow
    if (email || password) {
      if (!email || !password) {
        return output
          .error()
          .title('Please provide both email and password combination')
          .message()
          .exit('error')
      }

      try {
        const { accessToken, refreshToken } = await emailPasswordAuth({
          email,
          password,
        })
        setAuthData({ accessToken, refreshToken })

        output
          .info()
          .message(`Logged in successfully as ${as.cyan(email)}`)
          .print()
      } catch (err: any) {
        output.error().title(err.message).print()
      }
      return
    }

    // Get an available port
    const port = await getPort({
      port: config.auth.callback.preferredPortRange,
    })

    const loginUrl = `${config.projects.dashboard}/login?externalRedirect=cli&port=${port}`

    output
      .info()
      .message(`Please follow the link to login: ${as.cyan(loginUrl)}`)
      .print()

    // Try to open the login url
    openBrowser(loginUrl)

    try {
      // Wait for the user to finish with the login flow
      const { accessToken, refreshToken } = await generateCallbackEndpoint({
        port,
      })

      // Saving tokens locally
      setAuthData({ accessToken, refreshToken })

      const { email: userEmail } = decode(accessToken, accessTokenPayloadSchema)
      output
        .info()
        .message(`Logged in successfully as ${as.cyan(userEmail ?? '')}`)
        .print()
    } catch {
      return output
        .error()
        .title('Something went wrong. Please try again.')
        .exit('error')
    }
  })

  return cmd
}
